// src/pages/Settings.jsx
import React, { useEffect, useMemo, useRef, useState, useId } from 'react';
import api, { clearTokens } from '../api';
import { fmtDate as fmtTz } from '../utils/time';
import { resolveTenantHint, clearRememberedTenant } from '../utils/tenant';
import QRCode from 'qrcode';
import './settings.css';

/** ---------------- Force Light-only theme ---------------- */
const LIGHT_VARS = {
  '--bg': '#f8fafc',
  '--fg': '#0b1220',
  '--muted': '#475569',
  '--link': '#2563eb',
  '--accent': '#7c3aed',
  '--hairline': '#cbd5e1',
  '--card': '#ffffff',
  '--success': '#059669',
  '--danger': '#b91c1c',
  '--code-bg': '#f1f5f9',
};

function applyTheme(vars) {
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  // Preserve global wallpaper/gradient; rely on CSS vars for colors.
  document.body.style.color = vars['--fg'];
}

/** ---------------- Helpers ---------------- */
function sanitizeStateCode(v) {
  return String(v || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
}

/* ---------------- Password card helpers (HOISTED) ---------------- */

function getMe() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); }
  catch { return {}; }
}

function ShowBtn({ onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn outline small"
      style={{ whiteSpace: 'nowrap', minWidth: 56 }}
    >
      {active ? 'Hide' : 'Show'}
    </button>
  );
}

function InputRow({ id, label, type, value, onChange, autoComplete, onToggle, onShow }) {
  return (
    <div className="col">
      <label htmlFor={id}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          id={id}
          className="input"
          type={type}
          value={value}
          onChange={onChange}
          required
          autoComplete={autoComplete}
          style={{ flex: 1 }}
        />
        <ShowBtn onClick={onToggle} active={onShow} />
      </div>
    </div>
  );
}

/* ---------------- Password card (uses hoisted components) ---------------- */

function PasswordChangeCard() {
  const me = getMe();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState({ cur: false, nw: false, cf: false });
  const [infoOpen, setInfoOpen] = useState(false);

  const curId = useId();
  const nwId  = useId();
  const cfId  = useId();

  const toggle = (k) => setShow((s) => ({ ...s, [k]: !s[k] }));

  async function submit(e) {
    e.preventDefault();
    if (newPassword.length < 8) return alert('New password must be at least 8 characters');
    if (newPassword !== confirm) return alert('Passwords do not match');
    setSaving(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      setCurrentPassword(''); setNewPassword(''); setConfirm('');
      alert('Password changed successfully. You will need to log in again on other devices.');
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to change password';
      alert(msg);
    } finally { setSaving(false); }
  }

  return (
    <section className="card password-card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Change Password</h3>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen(true)}
          style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about changing passwords"
        >
          ℹ️
        </button>
      </div>
      {/* helps some browsers pair the right account with the password manager */}
      <input type="text" name="username" value={me?.email || ''} readOnly hidden autoComplete="username" />

      <form onSubmit={submit} className="column" style={{ gap: 12 }} autoComplete="on">
        <div className="grid auto-grid-3" style={{ gap: 12 }}>
          <InputRow
            id={curId}
            label="Current Password"
            type={show.cur ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            onToggle={() => toggle('cur')}
            onShow={show.cur}
          />
          <InputRow
            id={nwId}
            label="New Password"
            type={show.nw ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            onToggle={() => toggle('nw')}
            onShow={show.nw}
          />
          <InputRow
            id={cfId}
            label="Confirm New Password"
            type={show.cf ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            onToggle={() => toggle('cf')}
            onShow={show.cf}
          />
        </div>

        <div className="row end" style={{ marginTop: 8 }}>
          <button className="btn" disabled={saving} type="submit">
            {saving ? 'Saving...' : 'Update Password'}
          </button>
        </div>
      </form>

      {infoOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => setInfoOpen(false)}
        >
          <div className="card" style={{ maxWidth: 520, padding: 20 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Change Password</div>
              <button className="btn outline small" onClick={() => setInfoOpen(false)} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              <p>Your new password must be at least 8 characters long. Use a mix of letters, numbers and symbols for better security.</p>
              <p>After changing the password, you will remain signed in here but other active sessions may require re-login.</p>
              <ul style={{ paddingLeft: 20 }}>
                <li>Avoid reusing common passwords or easily guessable phrases.</li>
                <li>Use a password manager to generate and store strong passwords.</li>
                <li>If you suspect your account is compromised, change your password immediately.</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------------- Settings page ---------------- */

export default function Settings() {
  // Apply light theme once (and only once)
  useEffect(() => { applyTheme(LIGHT_VARS); }, []);
  const me = useMemo(() => getMe(), []);
  const tenantHint = useMemo(() => resolveTenantHint(), []);

  /** ---------------- Shop profile ---------------- */
  // include state_code in the form model; maps to DB setting `state_code`
  const [form, setForm] = useState({ shop_name: '', shop_address: '', shop_gstin: '', state_code: '', invoice_pdf_format: 'a4' });
  const [msg, setMsg] = useState('');
  const [infoOpen, setInfoOpen] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // lock flag & first-sale info
  const [originLocked, setOriginLocked] = useState(false);
  const [firstSaleInfo, setFirstSaleInfo] = useState(null);

  // server-provided URL (kept for reference), and a blob URL for <img>
  const [shopLogoUrl, setShopLogoUrl] = useState('');
  const [logoBlobUrl, setLogoBlobUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);

  /** ---------------- MFA ---------------- */
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaHasTotp, setMfaHasTotp] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaQr, setMfaQr] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaErr, setMfaErr] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaDisablePwd, setMfaDisablePwd] = useState('');
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [mfaDisableMethod, setMfaDisableMethod] = useState('totp');
  const [mfaDisableSending, setMfaDisableSending] = useState(false);

  // Fetch logo via Axios so auth + tenant headers are included
  async function refreshLogo() {
    try {
      const res = await api.get('/settings/logo', {
        responseType: 'blob',
        params: { v: Date.now(), tenant: tenantHint || undefined },
      });
      const url = URL.createObjectURL(res.data);
      setLogoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch {
      setLogoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ''; });
    }
  }

  async function onUploadLogo() {
    if (!logoFile) return;
    const fd = new FormData();
    fd.append('logo', logoFile);
    await api.post('/settings/logo', fd);
    await refreshLogo();
    setLogoFile(null);
  }
  async function onDeleteLogo() {
    await api.delete('/settings/logo');
    await refreshLogo(); // clears preview if not found
  }

  useEffect(() => {
    (async () => {
      // Load settings
      const { data } = await api.get('/settings');
      setForm({
        shop_name: data.shop_name || '',
        shop_address: data.shop_address || '',
        shop_gstin: data.shop_gstin || '',
        // prefer `state_code`; fallback to old `origin_state` if present
        state_code: sanitizeStateCode(data.state_code ?? data.origin_state ?? ''),
        invoice_pdf_format: (data.invoice_pdf_format === 'thermal' ? 'thermal' : 'a4'),
      });
      setShopLogoUrl(data.shop_logo_url || '');

      // Probe sales to decide lock (any sale means locked)
      try {
        const sres = await api.get('/sales', { params: { limit: 1 } });
        const arr = Array.isArray(sres.data) ? sres.data : [];
        if (arr.length > 0) {
          setOriginLocked(true);
          const s0 = arr[0];
          setFirstSaleInfo({
            invoice_no: s0?.invoice_no || '',
            created_at: s0?.created_at || s0?.sold_at || null,
          });
        } else {
          setOriginLocked(false);
          setFirstSaleInfo(null);
        }
      } catch {
        // On failure, fail open (do not lock due to probe error)
      }

      // Always fetch actual image through Axios to include headers
      refreshLogo();
    })();
    // cleanup: revoke object URL on unmount
    return () => { setLogoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ''; }); };
  }, []);

  async function refreshMfaStatus() {
    setMfaLoading(true);
    try {
      const { data } = await api.get('/auth/mfa/status');
      setMfaEnabled(!!data?.mfa_enabled);
      setMfaHasTotp(!!data?.has_totp);
    } catch (e) {
      console.error('MFA status failed', e);
    } finally {
      setMfaLoading(false);
    }
  }

  useEffect(() => { refreshMfaStatus(); }, []);

  async function startMfaSetup() {
    setMfaBusy(true);
    setMfaErr('');
    try {
      const { data } = await api.post('/auth/mfa/setup');
      setMfaSetup(data);
      setMfaCode('');
      const qr = await QRCode.toDataURL(data.otpauth_url, { width: 200, margin: 1 });
      setMfaQr(qr);
      await refreshMfaStatus();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to start MFA setup';
      setMfaErr(msg);
    } finally {
      setMfaBusy(false);
    }
  }

  async function enableMfa() {
    if (!mfaCode.trim()) return setMfaErr('Enter the code from your authenticator app');
    setMfaBusy(true);
    setMfaErr('');
    try {
      await api.post('/auth/mfa/enable', { code: mfaCode.trim() });
      setMfaSetup(null);
      setMfaQr('');
      setMfaCode('');
      await refreshMfaStatus();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to enable MFA';
      setMfaErr(msg);
    } finally {
      setMfaBusy(false);
    }
  }

  async function sendDisableEmailCode() {
    setMfaDisableSending(true);
    setMfaErr('');
    try {
      await api.post('/auth/mfa/email/send', { purpose: 'disable' });
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to send code';
      setMfaErr(msg);
    } finally {
      setMfaDisableSending(false);
    }
  }

  async function disableMfa() {
    if (!mfaDisablePwd.trim()) return setMfaErr('Password is required');
    if (!mfaDisableCode.trim()) return setMfaErr('Verification code is required');
    setMfaBusy(true);
    setMfaErr('');
    try {
      await api.post('/auth/mfa/disable', {
        password: mfaDisablePwd.trim(),
        code: mfaDisableCode.trim(),
        method: mfaDisableMethod,
      });
      setMfaDisablePwd('');
      setMfaDisableCode('');
      setMfaSetup(null);
      setMfaQr('');
      await refreshMfaStatus();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to disable MFA';
      setMfaErr(msg);
    } finally {
      setMfaBusy(false);
    }
  }

  async function save() {
    const payload = { ...form };
    const fmt = String(payload.invoice_pdf_format || '').toLowerCase();
    payload.invoice_pdf_format = fmt === 'thermal' ? 'thermal' : 'a4';

    if (!originLocked) {
      payload.state_code = sanitizeStateCode(payload.state_code);
      if (!/^[A-Z]{2}$/.test(payload.state_code)) {
        alert('Please enter a valid 2-letter state code (e.g., OD, KA, TN).');
        return;
      }
    } else {
      // If origin is locked, do not allow changing state_code via payload
      delete payload.state_code;
    }

    await api.put('/settings', payload);
    setMsg('Saved!');
    setTimeout(() => setMsg(''), 1800);
  }

  /** ---------------- Backup & Restore ---------------- */

  // Keeping for future: if you want to exclude on UI
  const EXCLUDED = ['user_sessions', 'password_reset_tokens', 'audit_logs'];

  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState({ phase: 'idle', value: 0 });
  const [lastResult, setLastResult] = useState(null);
  const fileInputRef = useRef(null);

  const [components, setComponents] = useState({
    // RBAC & users
    roles: true,
    permissions: true,
    role_permissions: true,
    users: true,
    user_roles: true,

    // Core & config
    settings: true,
    product_categories: true,
    products: true,
    customers: true,

    // Ops
    sales: true,
    returns: true,
  });

  const selectedList = useMemo(
    () => Object.entries(components).filter(([, v]) => v).map(([k]) => k),
    [components]
  );
  const toggle = (key) => setComponents((p) => ({ ...p, [key]: !p[key] }));

  // Helper to get token + tenant (fallbacks if interceptor not present)
  function authAndTenantHeaders() {
    const token =
      localStorage.getItem('accessToken') ||
      sessionStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('token') || '';
    const tenant = resolveTenantHint();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (tenant) headers['x-tenant'] = tenant;
    return headers;
  }

  const doExport = async () => {
    try {
      setBusy(true);
      setLastResult(null);
      setProgress({ phase: 'preparing', value: 10 });

      const tenant = resolveTenantHint();
      const params = {
        tables: selectedList.join(','),
        ...(tenant ? { tenant } : {}),
      };

      // Use axios so interceptors (if configured) add auth + x-tenant.
      // We also include explicit headers as a fallback.
      const resp = await api.get('/backup/export', {
        params,
        responseType: 'blob',
        headers: authAndTenantHeaders(),
        validateStatus: () => true,
      });

      if (resp.status !== 200) {
        let errMsg = `Export failed: ${resp.status}`;
        try {
          // Attempt to parse JSON error from blob
          const text = await resp.data.text?.();
          if (text) {
            const json = JSON.parse(text);
            errMsg = json.error || errMsg;
          }
        } catch {}
        throw new Error(errMsg);
      }

      setProgress({ phase: 'downloading', value: 70 });

      const ct = resp.headers['content-type'] || '';
      const isZip = ct.includes('zip');
      const ext = isZip ? 'zip' : 'json';
      const filename = `backup-${fmtTz(new Date(), 'YYYY-MM-DD-HH-mm-ss')}.${ext}`;

      const blob = resp.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      let summary = null;
      if (!isZip) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          summary = parsed.__meta || null;
        } catch {}
      }

      setLastResult({ type: 'export', ok: true, summary });
      setProgress({ phase: 'done', value: 100 });
    } catch (e) {
      setLastResult({ type: 'export', ok: false, message: e.message });
      setProgress(prev => ({ ...prev, phase: 'failed' }));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!file) return alert('Please choose a backup JSON file');
    try {
      setBusy(true);
      setLastResult(null);
      setProgress({ phase: 'reading file', value: 10 });

      const text = await file.text();
      const json = JSON.parse(text);

      const tenant = resolveTenantHint();
      const params = {
        tables: selectedList.join(','),
        ...(tenant ? { tenant } : {}),
      };

      setProgress({ phase: 'uploading', value: 30 });

      const resp = await api.post('/backup/import', json, {
        params,
        headers: { 'Content-Type': 'application/json', ...authAndTenantHeaders() },
        validateStatus: () => true,
      });

      if (resp.status !== 200) {
        const message = resp.data?.error || `Import failed: ${resp.status}`;
        throw new Error(message);
      }

      setProgress({ phase: 'processing', value: 75 });

      const result = resp.data || {};
      setLastResult({ type: 'import', ok: !!result.ok, result });
      setProgress({ phase: result.ok ? 'done' : 'completed with errors', value: 100 });
    } catch (e) {
      setLastResult({ type: 'import', ok: false, message: e.message });
      setProgress(prev => ({ ...prev, phase: 'failed' }));
    } finally {
      setBusy(false);
    }
  };

  const confirmLabel = useMemo(
    () => (form.shop_name || tenantHint || 'this shop/organization').trim(),
    [form.shop_name, tenantHint]
  );
  const confirmMatches = deleteConfirm.trim().toLowerCase() === confirmLabel.toLowerCase();
  const canDeleteTenant = useMemo(() => {
    const roles = Array.isArray(me?.roles) ? me.roles : [];
    const perms = Array.isArray(me?.perms) ? me.perms : [];
    return roles.includes('Owner') || perms.includes('settings:delete') || perms.includes('settings:delete_tenant');
  }, [me]);

  const handleDeleteTenant = async () => {
    if (!canDeleteTenant) return alert('You do not have permission to delete this shop / organization.');
    if (!confirmMatches) return alert(`Type "${confirmLabel}" to confirm deletion.`);
    if (!window.confirm('This will permanently delete all data for this shop / organization. This cannot be undone. Continue?')) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await api.delete('/settings/tenant');
      alert('Shop / organization deleted. You will be signed out.');
      clearTokens();
      clearRememberedTenant();
      window.location.assign('/login');
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to delete shop';
      setDeleteError(msg);
    } finally {
      setDeleteBusy(false);
    }
  };

  const hasLogo = !!logoBlobUrl;

  /** ---------------- Render ---------------- */
  return (
    <div className="container" style={{ marginTop: 16 }}>
      {/* Page Heading */}
      <h2 className="pageTitle" style={{ margin: '0 0 12px' }}>Settings</h2>

      {/* Shop Profile */}
      <section className="card" style={{ marginTop: 16 }}>
        <h3>Shop Profile</h3>

        {/* Fields grid */}
        <div className="grid twoCols gap12">
          <input
            className="input"
            placeholder="Shop Name"
            value={form.shop_name}
            onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
          />
          <input
            className="input"
            placeholder="GSTIN"
            value={form.shop_gstin}
            onChange={(e) => setForm({ ...form, shop_gstin: e.target.value })}
          />

          <textarea
            className="input"
            placeholder="Door no, street, city, pin code"
            value={form.shop_address}
            onChange={(e) => setForm({ ...form, shop_address: e.target.value })}
            style={{ gridColumn: '1 / -1', minHeight: 96 }}
          />

          {/* Origin State (for GST) — editable until first sale, then locked */}
          {!originLocked ? (
            <>
              <input
                className="input mono"
                placeholder="Please enter a valid 2-letter state code (e.g., OD, KA, TN)."
                value={sanitizeStateCode(form.state_code)}
                onChange={(e) => setForm({ ...form, state_code: sanitizeStateCode(e.target.value) })}
                style={{ gridColumn: '1 / -1', textTransform: 'uppercase' }}
                maxLength={2}
                title="Enter 2-letter state code (e.g., OD, KA, TN). Used for GST calculations."
              />
              <small style={{ gridColumn: '1 / -1', opacity: 0.8 }}>
                This is <code>state code</code> used for GST calculations.
                Once you make your first sale, it will be automatically locked and uneditable.
              </small>
            </>
          ) : (
            <div
              className="input"
              readOnly
              title="Locked after first sale"
              style={{ gridColumn: '1 / -1', display:'flex', alignItems:'center', justifyContent:'space-between' }}
            >
              <span>Origin State (for GST): <b>{form.state_code || '—'}</b></span>
              <span style={{ fontSize:12, opacity:0.75 }}>locked</span>
            </div>
          )}
        </div>

        {/* Optional banner when locked */}
        {originLocked && (
          <div
            className="card"
            style={{
              marginTop: 16,
              marginBottom: 12,
              padding: 10,
              background: 'var(--code-bg)',
            }}
          >
            <strong>Origin State is locked</strong>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
              You already have at least one sale
              {firstSaleInfo?.invoice_no ? ` (e.g., ${firstSaleInfo.invoice_no})` : ''}.
              For tax consistency, the Origin State (for GST) can’t be changed.
            </div>
          </div>
        )}

        {/* Invoice PDF format */}
        <div style={{ marginTop: 12 }}>
          <div className="row space" style={{ alignItems: 'center', gap: 8 }}>
            <div className="labelMuted" style={{ marginBottom: 0 }}>Invoices/Credit Notes PDF format - Choose how download/print render invoices/credit notes.</div>
          </div>
          <div className="grid autoFit210 gap12" style={{ marginTop: 6 }}>
            <label className="checkRow" style={{ alignItems: 'flex-start' }}>
              <input
                type="radio"
                name="invoice-format"
                value="a4"
                checked={form.invoice_pdf_format === 'a4'}
                onChange={() => setForm({ ...form, invoice_pdf_format: 'a4' })}
              />
              <div>
                <div style={{ fontWeight: 600 }}>A4 (detailed)</div>
                <div className="labelMuted">Full-width layout with item columns and signatures.</div>
              </div>
            </label>
            <label className="checkRow" style={{ alignItems: 'flex-start' }}>
              <input
                type="radio"
                name="invoice-format"
                value="thermal"
                checked={form.invoice_pdf_format === 'thermal'}
                onChange={() => setForm({ ...form, invoice_pdf_format: 'thermal' })}
              />
              <div>
                <div style={{ fontWeight: 600 }}>Thermal (80mm)</div>
                <div className="labelMuted">Compact receipt-style PDF for roll printers; still shows GST totals.</div>
              </div>
            </label>
          </div>
        </div>

        {/* Logo row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Shop Logo</div>
          <button
            type="button"
            className="btn btn-sm secondary"
            onClick={() => setInfoOpen('shop_logo')}
            style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
            title="Learn more about Shop Logo"
          >
            ℹ️
          </button>
        </div>
        <div className="logoRow">
          <div>
            <div className="labelMuted">Shop Logo</div>
            {hasLogo ? (
              <img className="logoImg" src={logoBlobUrl} alt="Shop Logo" />
            ) : (
              <div className="logoEmpty">No logo</div>
            )}
          </div>

          <div className="row vcenter gap8">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
            />
            <button className="btn" onClick={onUploadLogo} disabled={!logoFile}>Upload</button>
            {hasLogo && <button className="btn danger" onClick={onDeleteLogo}>Delete</button>}
          </div>
        </div>

        {/* Actions */}
        <div className="row end">
          <button className="btn" onClick={save}>Save</button>
        </div>
        {msg && <div style={{ color: 'var(--success)', marginTop: 6 }}>{msg}</div>}
      </section>

      {/* MFA */}
      <section className="card" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>Two-factor authentication (MFA)</h3>
          <button
            type="button"
            className="btn btn-sm secondary"
            onClick={() => setInfoOpen('mfa')}
            style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
            title="Learn more about MFA"
          >
            ℹ️
          </button>
        </div>

        {mfaLoading ? (
          <div style={{ color: 'var(--muted)' }}>Loading MFA status...</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>Status:</span>
              <span style={{ color: mfaEnabled ? 'var(--success,#059669)' : 'var(--muted,#475569)' }}>
                {mfaEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            {!mfaEnabled && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                  Use an authenticator app (Google Authenticator, Authy, Microsoft Authenticator).
                </div>
                <button className="btn" onClick={startMfaSetup} disabled={mfaBusy}>
                  {mfaBusy ? 'Preparing...' : mfaHasTotp ? 'Re-generate setup' : 'Start setup'}
                </button>

                {mfaSetup && (
                  <div className="card" style={{ padding: 12, background: 'var(--code-bg)' }}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {mfaQr && (
                        <img
                          src={mfaQr}
                          alt="Scan QR in authenticator app"
                          style={{ width: 160, height: 160, background: '#fff', padding: 8, borderRadius: 8 }}
                        />
                      )}
                      <div style={{ minWidth: 220 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Manual key</div>
                        <div className="mono" style={{ wordBreak: 'break-all' }}>{mfaSetup.secret}</div>
                        <div style={{ fontSize: 12, marginTop: 6, color: 'var(--muted)' }}>
                          If you cannot scan the QR, enter this key manually in your app.
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <label className="labelMuted">Enter code to enable</label>
                      <input
                        className="input"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        placeholder="123456"
                        inputMode="numeric"
                      />
                      <div className="row end" style={{ marginTop: 10 }}>
                        <button className="btn" onClick={enableMfa} disabled={mfaBusy}>
                          {mfaBusy ? 'Enabling...' : 'Enable MFA'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {mfaEnabled && (
              <div className="card" style={{ padding: 12, background: 'var(--code-bg)' }}>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
                  Disable MFA requires your password and a verification code.
                </div>

                <div className="grid twoCols gap12" style={{ alignItems: 'end' }}>
                  <div className="col">
                    <label className="labelMuted">Password</label>
                    <input
                      className="input"
                      type="password"
                      value={mfaDisablePwd}
                      onChange={(e) => setMfaDisablePwd(e.target.value)}
                    />
                  </div>

                  <div className="col">
                    <label className="labelMuted">Verification code</label>
                    <input
                      className="input"
                      value={mfaDisableCode}
                      onChange={(e) => setMfaDisableCode(e.target.value)}
                      placeholder="123456"
                      inputMode="numeric"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                  <label className="checkRow">
                    <input
                      type="radio"
                      name="mfa-disable-method"
                      value="totp"
                      checked={mfaDisableMethod === 'totp'}
                      onChange={() => setMfaDisableMethod('totp')}
                    />
                    <span>Authenticator app</span>
                  </label>
                  <label className="checkRow">
                    <input
                      type="radio"
                      name="mfa-disable-method"
                      value="email"
                      checked={mfaDisableMethod === 'email'}
                      onChange={() => setMfaDisableMethod('email')}
                    />
                    <span>Email code</span>
                  </label>
                  {mfaDisableMethod === 'email' && (
                    <button className="btn outline small" onClick={sendDisableEmailCode} disabled={mfaDisableSending}>
                      {mfaDisableSending ? 'Sending...' : 'Send code'}
                    </button>
                  )}
                </div>

                <div className="row end" style={{ marginTop: 12 }}>
                  <button className="btn danger" onClick={disableMfa} disabled={mfaBusy}>
                    {mfaBusy ? 'Disabling...' : 'Disable MFA'}
                  </button>
                </div>
              </div>
            )}

            {mfaErr && (
              <div style={{ color: 'var(--danger)', marginTop: 8 }}>{mfaErr}</div>
            )}
          </>
        )}
      </section>

      {/* Backup & Restore */}
      <section className="card" style={{ marginTop: 16, padding: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>Backup &amp; Restore</h3>
          <button
            type="button"
            className="btn btn-sm secondary"
            onClick={() => setInfoOpen('backup_restore')}
            style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
            title="Learn more about Backup & Restore"
          >
            ℹ️
          </button>
        </div>

        {/* Included (selectable) */}
        <div className="grid autoFit180 gap8">
          {Object.keys(components).map((k) => (
            <label key={k} className="checkRow">
              <input type="checkbox" checked={components[k]} onChange={() => toggle(k)} />
              <span style={{ textTransform: 'capitalize' }}>{k.replaceAll('_', ' ')}</span>
            </label>
          ))}
        </div>
        <small style={{ opacity: 0.7, display:'block', marginTop: 6 }}>
          Only selected components will be exported or restored.
        </small>

        {/* Grouped Export / Import with separator */}
        <div className="exportImportBar" style={{ marginTop: 8 }}>
          {/* Export group */}
          <div className="exportGroup">
            <button className="btn" onClick={doExport} disabled={busy}>
              Export
            </button>
          </div>

          {/* Vertical separator */}
          <div className="vSep" aria-hidden="true" />

          {/* Import group */}
          <div className="importGroup">
            <label htmlFor="backupFile" className="labelMuted" style={{ marginRight: 4 }}>
              Import:
            </label>

            {/* Use label-as-button for consistent styling with buttons */}
            <label className={`btn outline ${busy ? 'disabled' : ''}`} style={{ position: 'relative' }}>
              Browse…
              <input
                id="backupFile"
                ref={fileInputRef}
                type="file"
                accept=".json,.zip,application/json,application/zip"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={busy}
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0,
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
                aria-label="Choose backup JSON or ZIP file"
              />
            </label>
              
            <span className="fileName" title={file?.name || ''}>
              {file?.name || 'No file selected'}
            </span>
              
            <button
              className="btn"
              onClick={doImport}
              disabled={busy || !file}
              aria-disabled={busy || !file}
            >
              Import
            </button>
          </div>
        </div>

        {(busy || progress.phase !== 'idle') && (
          <div style={{ marginTop: 12 }}>
            <div className="row space">
              <small>{progress.phase}</small>
              <small>{progress.value}%</small>
            </div>
            <div className="progressTrack">
              <div className="progressFill" style={{ width: `${progress.value}%` }} />
            </div>
          </div>
        )}

        {lastResult && (
          <div className="card" style={{ marginTop: 12, padding: 12, borderLeft: `4px solid ${lastResult.ok ? 'var(--success)' : 'var(--danger)'}` }}>
            <strong style={{ display: 'block', marginBottom: 6 }}>
              {lastResult.type === 'export' ? 'Export' : 'Import'} {lastResult.ok ? 'successful' : 'failed'}
            </strong>

            {lastResult.message && <div style={{ marginBottom: 8 }}>{lastResult.message}</div>}

            {lastResult.type === 'export' && lastResult.summary && (
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                Generated: {fmtTz(lastResult.summary.generated_at)} · Components: {lastResult.summary.total_components} ·{' '}
                {lastResult.summary.status?.map((s) => `${s.component}:${s.rows}`).join('  ')}
              </div>
            )}

            {lastResult.type === 'import' && lastResult.result && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.9 }}>
                  Started: {fmtTz(lastResult.result.started_at)} · Completed: {fmtTz(lastResult.result.completed_at)}
                </div>
                <div style={{ marginTop: 6 }}>
                  {Object.entries(lastResult.result.totals || {}).map(([comp, info]) => (
                    <div key={comp} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                      <strong style={{ minWidth: 160, textTransform: 'capitalize' }}>{comp.replaceAll('_', ' ')}</strong>
                      <span>Attempted: {info.attempted}</span>
                      <span>Success: {info.succeeded}</span>
                      <span>Failed: {info.failed}</span>
                      <span>({info.elapsed_ms} ms)</span>
                    </div>
                  ))}
                </div>
                {Array.isArray(lastResult.result.errors) && lastResult.result.errors.length > 0 && (
                  <details style={{ marginTop: 8 }}>
                    <summary>Show error details ({lastResult.result.errors.length})</summary>
                    <div className="errorBox">
                      <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                        {JSON.stringify(lastResult.result.errors, null, 2)}
                      </pre>
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {canDeleteTenant && (
        <section
          className="card dangerCard"
          style={{ marginTop: 16, borderColor: 'var(--danger)' }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <h3 style={{ marginTop: 0, marginBottom: 0 }}>Delete Shop / Organization</h3>
            <button
              type="button"
              className="btn btn-sm secondary"
              onClick={() => setInfoOpen('delete_tenant')}
              style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
              title="Learn more about deleting a shop"
            >
              ℹ️
            </button>
          </div>
          <p style={{ marginTop: 4, color: 'var(--muted)' }}>
            Permanently delete all data for <strong>{confirmLabel}</strong>. This action cannot be undone.
          </p>
          <div className="grid twoCols gap12" style={{ alignItems: 'end', marginTop: 12 }}>
            <div className="col">
              <label className="labelMuted">Type "{confirmLabel}" to confirm</label>
              <input
                className="input"
                placeholder={confirmLabel}
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
              />
            </div>
            <div className="col" style={{ gap: 8 }}>
              <button
                className="btn danger"
                onClick={handleDeleteTenant}
                disabled={!confirmMatches || deleteBusy}
              >
                {deleteBusy ? 'Deleting...' : 'Delete shop & data'}
              </button>
              <small style={{ color: 'var(--danger)' }}>This cannot be undone.</small>
              {deleteError && <div style={{ color: 'var(--danger)', marginTop: 4 }}>{deleteError}</div>}
            </div>
          </div>
        </section>
      )}

      {/* Password (uniform card) */}
      <PasswordChangeCard />

      {/* Info Modals */}
      {infoOpen === 'shop_logo' && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setInfoOpen(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', padding: 24,
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Shop Logo</div>
              <button
                className="btn btn-sm secondary"
                onClick={() => setInfoOpen(null)}
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text, #0f172a)' }}>
              <p><strong>Shop Logo</strong> is your brand's visual representation that appears in invoices, receipts, and throughout your system.</p>
              <p><strong>Supported formats:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>PNG (recommended - supports transparency)</li>
                <li>JPEG</li>
                <li>WebP</li>
              </ul>
              <p><strong>Best practices:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Use square dimensions (e.g., 200x200px)</li>
                <li>Keep file size under 2MB</li>
                <li>Use high resolution for print quality</li>
                <li>PNG with transparent background is ideal for invoices</li>
              </ul>
              <p><strong>Where it's used:</strong></p>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Invoices printouts</li>
                <li>Credit Notes printouts</li>
                <li>Export Invoice / Credit Notes ZIP</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {infoOpen === 'backup_restore' && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setInfoOpen(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', padding: 24,
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Backup &amp; Restore</div>
              <button
                className="btn btn-sm secondary"
                onClick={() => setInfoOpen(null)}
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text, #0f172a)' }}>
              <p><strong>Backup &amp; Restore</strong> allows you to securely save and restore your business data.</p>
              <p><strong>What you can backup:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Roles & permissions</li>
                <li>Users and access control</li>
                <li>Product categories</li>
                <li>Products and inventory</li>
                <li>Customers and sales history</li>
                <li>Settings and configuration</li>
              </ul>
              <p><strong>Backup export:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Select which components to include</li>
                <li>Download as JSON or ZIP format</li>
                <li>Keep multiple backups for safety</li>
              </ul>
              <p><strong>Restore import:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Choose previously exported backup file</li>
                <li>Merge or overwrite existing data</li>
                <li>Verify data before confirming restore</li>
              </ul>
              <p><strong>Important notes:</strong></p>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Always keep backups on external storage</li>
                <li>Schedule regular backups (weekly recommended)</li>
                <li>Test restore process periodically</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {infoOpen === 'mfa' && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setInfoOpen(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', padding: 24,
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Two-factor authentication (MFA)</div>
              <button
                className="btn btn-sm secondary"
                onClick={() => setInfoOpen(null)}
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text, #0f172a)' }}>
              <p><strong>MFA</strong> adds a second step to protect your account, even if your password is compromised.</p>
              <p><strong>How it works:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Scan the QR code in your authenticator app</li>
                <li>Enter the 6-digit code to enable MFA</li>
                <li>Use a fresh code each time you sign in</li>
              </ul>
              <p><strong>Tips:</strong></p>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Install the authenticator app on a secure device</li>
                <li>Keep access to the device before enabling</li>
                <li>If you lose the device, contact an admin to recover access</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {infoOpen === 'delete_tenant' && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setInfoOpen(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', padding: 24,
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Delete Shop / Organization</div>
              <button
                className="btn btn-sm secondary"
                onClick={() => setInfoOpen(null)}
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text, #0f172a)' }}>
              <p><strong>This action is permanent.</strong> Deleting a shop removes all data tied to this organization.</p>
              <p><strong>Before you delete:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Export a backup of critical data</li>
                <li>Confirm no active users are using the shop</li>
                <li>Double-check the shop name shown for confirmation</li>
              </ul>
              <p><strong>What is removed:</strong></p>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Users, roles, and permissions</li>
                <li>Products, inventory, customers, and sales</li>
                <li>Settings, invoices, and reports</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
